import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import Backend from "i18next-http-backend";
import LanguageDetector from "i18next-browser-languagedetector";

export const LOCALE_STORAGE_KEY = "cepaas.locale";

const FALLBACK_LOCALE = "en";
let supportedLocales = [FALLBACK_LOCALE];
let localesLoadPath = null;

export function getSupportedLocales() {
  return supportedLocales;
}

function setSupportedLocales(codes) {
  if (codes?.length > 0) {
    supportedLocales = codes;
    if (i18n.isInitialized) {
      i18n.options.supportedLngs = [...codes, FALLBACK_LOCALE];
    }
  }
}

export function getCurrentLocale() {
  const stored = localStorage.getItem(LOCALE_STORAGE_KEY);
  if (stored && supportedLocales.includes(stored)) {
    return stored;
  }
  const lang = i18n.language || FALLBACK_LOCALE;
  const match = supportedLocales.find(
    (l) => lang === l || lang.startsWith(l + "-")
  );
  return match || FALLBACK_LOCALE;
}

export async function changeLocale(lng) {
  const next = supportedLocales.includes(lng) ? lng : FALLBACK_LOCALE;
  localStorage.setItem(LOCALE_STORAGE_KEY, next);
  document.documentElement.lang = next;
  await i18n.changeLanguage(next);
  return next;
}

/**
 * Fetch supported locales from GET {base}/i18n/locales where base is derived from loadPath.
 * loadPath example: /auth/rest/i18n/{{lng}}/{{ns}}
 */
export async function fetchLocales(explicitLocalesUrl) {
  const url =
    explicitLocalesUrl ||
    (localesLoadPath
      ? localesLoadPath.replace("/{{lng}}/{{ns}}", "/locales").replace("{{lng}}/{{ns}}", "locales")
      : null);
  if (!url) {
    return supportedLocales;
  }
  try {
    const res = await fetch(url, { credentials: "same-origin", cache: "no-cache" });
    if (!res.ok) {
      return supportedLocales;
    }
    const data = await res.json();
    const codes = (data.locales || [])
      .map((item) => (typeof item === "string" ? item : item.code))
      .filter(Boolean);
    setSupportedLocales(codes);
  } catch (e) {
    // keep fallback
  }
  return supportedLocales;
}

export async function initI18n(loadPath) {
  if (i18n.isInitialized) {
    return i18n;
  }

  localesLoadPath = loadPath;
  await fetchLocales();

  await i18n
    .use(Backend)
    .use(LanguageDetector)
    .use(initReactI18next)
    .init({
      fallbackLng: FALLBACK_LOCALE,
      supportedLngs: [...supportedLocales, FALLBACK_LOCALE],
      nonExplicitSupportedLngs: true,
      ns: ["messages"],
      defaultNS: "messages",
      keySeparator: false,
      nsSeparator: false,
      interpolation: {
        escapeValue: false,
      },
      backend: {
        loadPath,
      },
      detection: {
        order: ["localStorage", "navigator"],
        lookupLocalStorage: LOCALE_STORAGE_KEY,
        caches: ["localStorage"],
      },
      react: {
        useSuspense: false,
      },
    });

  const locale = getCurrentLocale();
  document.documentElement.lang = locale;
  if (i18n.language !== locale) {
    await i18n.changeLanguage(locale);
  }

  return i18n;
}

export default i18n;
